import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  HStack,
  Link,
  Text,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { registerDevTool } from '../../registry';
import { client } from '@php-beam/packages/frontend/src/api/client';
import LeadSearch from '../ResourceComposer/LeadSearch';

interface ActiveLead {
  id: number;
  business_name: string;
  url: string;
}

interface EnrollmentInfo {
  id: number;
  ppm_created_at: string | null;
  bound_at: string | null;
  bind_sent_at: string | null;
  created_at: string;
}

function EnrollmentTools() {
  const [activeLead, setActiveLead] = useState<ActiveLead | null>(null);

  return (
    <VStack align="stretch" spacing={4}>
      <Box>
        <Text fontSize="xs" fontWeight="semibold" color="gray.500" mb={1}>
          Select lead
        </Text>
        <LeadSearch
          onSelect={(lead) =>
            setActiveLead({ id: lead.id, business_name: lead.business_name, url: lead.url })
          }
        />
      </Box>

      {activeLead && (
        <Alert status="success" borderRadius="md">
          <AlertIcon />
          <Text fontSize="sm">
            <Link href={activeLead.url} fontWeight="bold" color="purple.600">
              {activeLead.business_name}
            </Link>{' '}
            (ID: {activeLead.id})
          </Text>
          <Button
            size="xs"
            ml="auto"
            variant="ghost"
            onClick={() => setActiveLead(null)}
          >
            Clear
          </Button>
        </Alert>
      )}

      {activeLead && <EnrollmentPanel lead={activeLead} />}
    </VStack>
  );
}

function EnrollmentPanel({ lead }: { lead: ActiveLead }) {
  const [enrollment, setEnrollment] = useState<EnrollmentInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const toast = useToast();

  const refresh = useCallback(() => {
    setLoading(true);
    client
      .get<{ enrollment: EnrollmentInfo | null }>(`/devtools/enrollment/lead/${lead.id}`)
      .then((res) => setEnrollment(res.data.enrollment))
      .finally(() => setLoading(false));
  }, [lead.id]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  if (loading) return <Text fontSize="sm">Loading...</Text>;

  if (!enrollment) {
    return (
      <Alert status="warning" borderRadius="md">
        <AlertIcon />
        <Text fontSize="sm">No enrollment found for this lead.</Text>
      </Alert>
    );
  }

  return (
    <VStack align="stretch" spacing={4}>
      <HStack>
        <Text fontWeight="semibold" fontSize="sm">
          Enrollment #{enrollment.id}
        </Text>
        <Badge colorScheme={enrollment.ppm_created_at ? 'green' : 'gray'} fontSize="xs">
          {enrollment.ppm_created_at ? 'PPM Reviewed' : 'PPM Pending'}
        </Badge>
        <Badge colorScheme={enrollment.bound_at ? 'green' : 'gray'} fontSize="xs">
          {enrollment.bound_at ? 'Bound' : 'Not Bound'}
        </Badge>
      </HStack>

      <PPMReviewSection
        leadId={lead.id}
        ppmCreatedAt={enrollment.ppm_created_at}
        onDone={refresh}
      />
    </VStack>
  );
}

function PPMReviewSection({
  leadId,
  ppmCreatedAt,
  onDone,
}: {
  leadId: number;
  ppmCreatedAt: string | null;
  onDone: () => void;
}) {
  const [submitting, setSubmitting] = useState(false);
  const toast = useToast();

  const markReviewed = async () => {
    setSubmitting(true);
    try {
      await client.post(`/devtools/enrollment/lead/${leadId}/ppm-review`);
      toast({
        title: 'PPM marked as reviewed',
        status: 'success',
        duration: 3000,
      });
      onDone();
    } catch (err: any) {
      toast({
        title: 'Failed to mark PPM',
        description: err?.response?.data?.message || 'Unknown error',
        status: 'error',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box p={3} borderWidth={1} borderRadius="md" borderColor="gray.200">
      <HStack mb={2}>
        <Text fontWeight="semibold" fontSize="sm">
          PPM Review
        </Text>
        {ppmCreatedAt && (
          <Badge colorScheme="green" fontSize="xs">
            Reviewed
          </Badge>
        )}
      </HStack>

      <Button
        size="sm"
        colorScheme="purple"
        onClick={markReviewed}
        isLoading={submitting}
      >
        Mark PPM as Reviewed
      </Button>
    </Box>
  );
}

registerDevTool({
  name: 'Enrollment',
  component: EnrollmentTools,
});
